import { useMemo, useState } from 'react';
import Modal from './Modal';
import { useToast } from './Toast';
import { db } from '../firebaseConfig';
import { createEntity } from '../data/entities';
import { appendAudit, loadVersionClauses, docViewOf } from '../data/deals';

// Signature envelope builder — "Request signatures" over one or more of the
// project's documents. Clause-anchored fields: signature blocks are detected
// from section headings (/signat|execution/i), each getting a signature +
// date tab per signer; an optional per-envelope toggle adds initials on
// every other section. The envelope pins each doc's CURRENT version; the
// send worker verifies the version is sealed and stamps its checkpoint
// hashes, so what's signed is a hashable reference into the version chain.
//
// Writing status:'queued' IS the send action (Firestore-as-queue — the
// envelope worker picks it up, generates tokens, and emails signing links).

const SIG_HEADING_RE = /signat|execution/i;

const cleanEmail = (s) => String(s || '').trim().toLowerCase();
const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export default function EnvelopeBuilder({ scope, deal, docs, user, participant, onClose }) {
  const toast = useToast();
  const signable = useMemo(
    () => (docs || []).filter((d) => d.status === 'ready' && d.currentVersionId
      && docViewOf(d) !== 'deck' && docViewOf(d) !== 'workbook'
      && !(d.tags || []).includes('executed') && !d.supersededBy),
    [docs],
  );
  const [title, setTitle] = useState(`${deal?.name || 'Agreement'} — signature package`);
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [signers, setSigners] = useState([{ name: '', email: '', partyKey: '' }]);
  const [initialsAll, setInitialsAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { fields, byDoc: {docId: {sigClauses, total}} }

  const toggleDoc = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
    setPreview(null);
  };

  const setSigner = (i, patch) => {
    setSigners((rows) => rows.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
    setPreview(null);
  };

  // Load each selected doc's current version and place clause-anchored tabs.
  const detectFields = async () => {
    const chosen = signable.filter((d) => selected.has(d.id));
    const people = signers
      .map((s, i) => ({ key: `s${i + 1}`, order: i + 1, name: s.name.trim(), email: cleanEmail(s.email), partyKey: s.partyKey || '' }))
      .filter((s) => s.name && validEmail(s.email));
    if (!chosen.length) throw new Error('select at least one document');
    if (!people.length) throw new Error('add at least one signer (name + valid email)');
    const dupe = new Set();
    for (const p of people) {
      if (dupe.has(p.email)) throw new Error(`duplicate signer email: ${p.email}`);
      dupe.add(p.email);
    }

    const fields = [];
    const byDoc = {};
    for (const d of chosen) {
      // eslint-disable-next-line no-await-in-loop
      const vSnap = await db.doc(`orgs/${scope.org}/deals/${scope.deal}/docs/${d.id}/versions/${d.currentVersionId}`).get();
      if (!vSnap.exists) throw new Error(`current version of "${d.title}" not found`);
      // eslint-disable-next-line no-await-in-loop
      const clauses = await loadVersionClauses({ id: vSnap.id, ...vSnap.data() });
      let sigClauses = clauses.filter((c) => SIG_HEADING_RE.test(c.heading || ''));
      // No signature-block heading: anchor to the final section.
      if (!sigClauses.length && clauses.length) sigClauses = [clauses[clauses.length - 1]];
      for (const c of sigClauses) {
        for (const p of people) {
          fields.push({ id: `f-${d.id}-${c.id}-${p.key}-sig`, docId: d.id, clauseId: c.id, signerKey: p.key, type: 'signature', required: true });
          fields.push({ id: `f-${d.id}-${c.id}-${p.key}-date`, docId: d.id, clauseId: c.id, signerKey: p.key, type: 'date', required: true });
        }
      }
      if (initialsAll) {
        const sigIds = new Set(sigClauses.map((c) => c.id));
        for (const c of clauses.filter((x) => !sigIds.has(x.id))) {
          for (const p of people) {
            fields.push({ id: `f-${d.id}-${c.id}-${p.key}-init`, docId: d.id, clauseId: c.id, signerKey: p.key, type: 'initials', required: true });
          }
        }
      }
      byDoc[d.id] = {
        title: d.title,
        versionId: d.currentVersionId,
        sigHeadings: sigClauses.map((c) => c.heading || '(untitled section)'),
        sectionCount: clauses.length,
      };
    }
    return { fields, byDoc, people, chosen };
  };

  const runDetect = async () => {
    setBusy(true);
    try {
      const det = await detectFields();
      setPreview(det);
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const det = preview || (await detectFields());
      const envDocs = det.chosen.map((d) => ({ docId: d.id, versionId: d.currentVersionId, title: d.title }));
      const envelopeId = await createEntity('envelope', scope, {
        title: title.trim() || 'Signature request',
        message: message.trim(),
        status: 'queued',
        docs: envDocs,
        signers: det.people.map((p) => ({ ...p, status: 'pending' })),
        fields: det.fields,
        createdBy: user.uid,
        createdByName: user.displayName || user.email || '',
      });
      await appendAudit(
        scope,
        { byUid: user.uid, byName: user.displayName || user.email || '', partyKey: participant?.partyKey || '' },
        'envelope.queued',
        `Signature envelope "${title.trim()}" queued — ${envDocs.length} document(s), ${det.people.length} signer(s)`,
        { envelopeId },
      );
      toast('Signature request queued — signers are emailed their links in order.', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Request signatures" onClose={onClose} maxWidth={720}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
        <div>
          <div className="cs-field">
            <label htmlFor="env-title">Envelope title</label>
            <input id="env-title" className="cs-input" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="cs-field" style={{ marginBottom: 0 }}>
            <label htmlFor="env-msg">Message to signers (optional)</label>
            <textarea id="env-msg" className="cs-input" rows={2} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>

        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <label style={{ fontSize: 13, color: 'var(--muted)' }}>Documents ({selected.size} selected)</label>
          {signable.length === 0 && (
            <div className="cs-empty">No signable documents — a document must be ready with a published version.</div>
          )}
          <div className="u-stack" style={{ '--gap': 'var(--sp-1)', maxHeight: 220, overflowY: 'auto' }}>
            {signable.map((d) => (
              <label key={d.id} className="u-cluster" style={{ gap: 'var(--sp-2)', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleDoc(d.id)} />
                <span className="u-small" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</span>
                <span className="u-xs u-muted">v{d.versionCount || 1}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <label style={{ fontSize: 13, color: 'var(--muted)' }}>Signers (sign in this order)</label>
          {signers.map((s, i) => (
            <div key={i} className="u-cluster" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
              <span className="u-xs u-muted" style={{ width: 16 }}>{i + 1}.</span>
              <input className="cs-input" placeholder="Full name" value={s.name} style={{ flex: 1 }} onChange={(e) => setSigner(i, { name: e.target.value })} />
              <input className="cs-input" placeholder="Email" type="email" value={s.email} style={{ flex: 1.2 }} onChange={(e) => setSigner(i, { email: e.target.value })} />
              <select className="cs-input" value={s.partyKey} style={{ width: 130 }} onChange={(e) => setSigner(i, { partyKey: e.target.value })}>
                <option value="">No party</option>
                {(deal?.parties || []).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              {signers.length > 1 && (
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setSigners((r) => r.filter((_, ri) => ri !== i)); setPreview(null); }} aria-label="Remove signer">✕</button>
              )}
            </div>
          ))}
          <div>
            <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setSigners((r) => [...r, { name: '', email: '', partyKey: '' }])}>+ Add signer</button>
          </div>
        </div>

        <label className="u-cluster" style={{ gap: 'var(--sp-2)', alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={initialsAll} onChange={(e) => { setInitialsAll(e.target.checked); setPreview(null); }} />
          <span className="u-small">Require initials on every section (in addition to the signature blocks)</span>
        </label>

        {preview && (
          <div className="cs-card" style={{ padding: 'var(--sp-3)' }}>
            <div className="u-small" style={{ fontWeight: 600, marginBottom: 'var(--sp-2)' }}>
              {preview.fields.length} signing tab{preview.fields.length === 1 ? '' : 's'} across {Object.keys(preview.byDoc).length} document{Object.keys(preview.byDoc).length === 1 ? '' : 's'}
            </div>
            <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
              {Object.entries(preview.byDoc).map(([id, d]) => (
                <div key={id} className="u-xs u-muted">
                  <strong style={{ color: 'var(--text)' }}>{d.title}</strong> — signature block: {d.sigHeadings.join('; ')}
                  {initialsAll ? ` · initials on ${d.sectionCount - d.sigHeadings.length} other sections` : ''}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="u-cluster" style={{ gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
          <button className="cs-btn cs-btn-outline" disabled={busy} onClick={runDetect}>Preview signing tabs</button>
          <button className="cs-btn cs-btn-primary" disabled={busy || !selected.size} onClick={send}>
            {busy ? 'Working…' : 'Send for signatures'}
          </button>
        </div>
        <p className="u-xs u-muted" style={{ margin: 0 }}>
          Signers receive an email link and verify with a one-time code — no account needed.
          Each signature is timestamped and sealed into a SHA-256 evidence chain pinned to the
          exact document versions; a certificate of completion is attached to the executed copies.
        </p>
      </div>
    </Modal>
  );
}
